// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-352 · COMPARTIR UNA CONVERSACIÓN DE WHATSAPP CON EL EQUIPO (26-sep-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «Boton de compartir chat para mensaje a miembro del equipo», y el 26-sep: «eliges a quién y le llega por el
//  chat de equipo el enlace a esa conversación, con una nota tuya» y «un enlace automatico que redirija a esa conversación
//  de whatsapp». Esta ventana elige a quién (solo salen quienes pueden ver las conversaciones de WhatsApp: lo decide el
//  servidor, GET /conversaciones/compartir/equipo) y manda POST /conversaciones/:id/compartir. El mensaje y el enlace los
//  compone el servidor (routes/conversaciones).
import { useEffect, useState } from 'react'
import api from '../../services/api'
import { useToast } from '../../context/ToastContext'
import { msgError } from '../../utils/errores'
import { Ico, Boton } from '../ui'
import { t } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Lo que dice la ventana al acabar. Pura: la mide la guardia.
export function avisoCompartida(n) {
  return t('components.compartir.CompartirConversacion.plural_compartida_con_personas_por', { one: 'Compartida por el chat de equipo', other: 'Compartida con {{count}} personas por el chat de equipo' }, { count: n, _forma: (n === 1) ? 'one' : 'other' })
}

export default function CompartirConversacion({ conv, onCerrar }) {
  const toast = useToast()
  const [equipo, setEquipo] = useState(null)
  const [elegidos, setElegidos] = useState([])
  const [nota, setNota] = useState('')
  const [enviando, setEnviando] = useState(false)
  useEffect(() => {
    let vivo = true
    api.get('/conversaciones/compartir/equipo')
      .then((r) => { if (vivo) setEquipo(Array.isArray(r.data?.equipo) ? r.data.equipo : []) })
      .catch(() => { if (vivo) setEquipo([]) })
    return () => { vivo = false }
  }, [])
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onCerrar() }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [onCerrar])

  const alternar = (id) => setElegidos((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]))
  const compartir = async () => {
    if (!elegidos.length || enviando) return
    setEnviando(true)
    try {
      const r = await api.post('/conversaciones/' + conv.id + '/compartir', { para: elegidos, nota })
      toast.success(avisoCompartida(Array.isArray(r.data?.enviados) ? r.data.enviados.length : elegidos.length))
      onCerrar()
    } catch (e) {
      toast.error(msgError(e, t('components.compartir.CompartirConversacion.no_se_pudo_compartir_la', 'No se pudo compartir la conversación')))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div data-compartir-conversacion onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="compartir-conv-titulo" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(440px, 100%)', maxHeight: 'calc(90vh / var(--z, 1))', overflowY: 'auto', background: 'var(--surface)', color: 'var(--text-primary)', borderRadius: 14, border: '1px solid var(--border)', boxShadow: '0 18px 50px rgba(0, 0, 0, 0.25)', padding: 18, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Ico n="send" s={18} />
          <h2 id="compartir-conv-titulo" style={{ margin: 0, fontSize: 16 }}>{t('components.compartir.CompartirConversacion.compartir_con_el_equipo', 'Compartir con el equipo')}</h2>
          <button type="button" onClick={onCerrar} aria-label={t('components.compartir.CompartirConversacion.cerrar', 'Cerrar')}
            style={{ marginLeft: 'auto', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          {t('components.compartir.CompartirConversacion.la_conversacion_de_whatsapp_con', 'La conversación de WhatsApp con ')}<b>{conv.nombre || t('components.compartir.CompartirConversacion.este_paciente', 'este paciente')}</b>{t('components.compartir.CompartirConversacion.le_llega_por_el_chat', ' le llega por el chat de equipo, con tu nota y un enlace que la abre. Solo salen las personas que pueden ver las conversaciones de WhatsApp.')}
        </p>
        {equipo === null ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('components.compartir.CompartirConversacion.cargando_el_equipo', 'Cargando el equipo…')}</div>
        ) : !equipo.length ? (
          <div data-compartir-sin-nadie style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('components.compartir.CompartirConversacion.no_hay_nadie_mas_en', 'No hay nadie más en el equipo que pueda ver las conversaciones de WhatsApp.')}</div>
        ) : (
          <div role="group" aria-label={t('components.compartir.CompartirConversacion.a_quien_se_la_compartes', 'A quién se la compartes')} style={{ display: 'grid', gap: 4, maxHeight: 240, overflowY: 'auto' }}>
            {equipo.map((p) => (
              <label key={p.id} data-compartir-persona={p.id}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-light, var(--border))', cursor: 'pointer', fontSize: 13.5 }}>
                <input type="checkbox" checked={elegidos.includes(p.id)} onChange={() => alternar(p.id)} style={{ width: 16, height: 16 }} />
                <span>{p.nombre}</span>
              </label>
            ))}
          </div>
        )}
        <label htmlFor="compartir-conv-nota" style={{ fontSize: 12.5, fontWeight: 600 }}>{t('components.compartir.CompartirConversacion.tu_nota_opcional', 'Tu nota (opcional)')}</label>
        <textarea id="compartir-conv-nota" value={nota} onChange={(e) => setNota(e.target.value)} maxLength={500} rows={3}
          placeholder={t('components.compartir.CompartirConversacion.por_ejemplo_pregunta_por_el', 'Por ejemplo: pregunta por el presupuesto, ¿se lo miras tú?')}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Boton type="button" onClick={onCerrar}>{t('components.compartir.CompartirConversacion.cancelar', 'Cancelar')}</Boton>
          <Boton type="button" variante="primario" icon="send" data-compartir-enviar onClick={compartir} disabled={!elegidos.length || enviando}>
            {enviando ? t('components.compartir.CompartirConversacion.compartiendo', 'Compartiendo…') : t('components.compartir.CompartirConversacion.compartir', 'Compartir')}
          </Boton>
        </div>
      </div>
    </div>
  )
}
